/**
 * Hooks extracted from SearchBar.jsx — pure state + side-effects, no UI.
 * Keeps SearchBar.jsx focused on render/layout.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RADIUS_KM = ["5km", "10km", "20km", "30km", "50km", "75km", "100km"];
export const RADIUS_MI = ["5mi", "10mi", "20mi", "30mi", "50mi", "75mi", "100mi"];
export const ADD_NEW_VALUE = "__ADD_NEW__";
const KM_TO_MI = 0.621371;

export function convertRadius(value, toUnit) {
  const m = String(value || "").match(/^(\d+(?:\.\d+)?)(km|mi)$/i);
  if (!m) return 20;
  const n = parseFloat(m[1]);
  const fromUnit = m[2].toLowerCase();
  if (fromUnit === toUnit) return n;
  if (fromUnit === "km" && toUnit === "mi") return n * KM_TO_MI;
  if (fromUnit === "mi" && toUnit === "km") return n / KM_TO_MI;
  return n;
}

export function snapToOption(numericValue, options) {
  let best = options[0];
  let bestDiff = Infinity;
  for (const opt of options) {
    const m = opt.match(/^(\d+(?:\.\d+)?)/);
    const n = m ? parseFloat(m[1]) : 0;
    const diff = Math.abs(n - numericValue);
    if (diff < bestDiff) { bestDiff = diff; best = opt; }
  }
  return best;
}

/** Manages km↔mi unit + radius value, snapping when the unit toggles. */
export function useRadius(initial = "20km") {
  const [unit, setUnit] = useState("km");
  const [radius, setRadius] = useState(initial);
  const switchUnit = (u) => {
    if (u === unit) return;
    const targetOptions = u === "mi" ? RADIUS_MI : RADIUS_KM;
    const numeric = convertRadius(radius, u);
    setRadius(snapToOption(numeric, targetOptions));
    setUnit(u);
  };
  const radiusOptions = unit === "mi" ? RADIUS_MI : RADIUS_KM;
  return { unit, radius, setRadius, switchUnit, radiusOptions };
}

/** Manages business-types catalog + add/delete custom types. */
export function useBusinessTypes(t) {
  const [types, setTypes] = useState([]);
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [savingType, setSavingType] = useState(false);

  const reloadTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/business-types`);
      setTypes(data.types || []);
      setBuiltin(data.builtin || []);
      setCustom(data.custom || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { reloadTypes(); }, [reloadTypes]);

  const saveNewType = useCallback(async (name, onSaved) => {
    const trimmed = (name || "").trim();
    if (!trimmed || trimmed.length < 2) {
      toast.error(t("search.type_too_short"));
      return null;
    }
    setSavingType(true);
    try {
      const { data } = await axios.post(`${API}/business-types`, { name: trimmed });
      toast.success(t("search.type_added", { name: data.type?.name || trimmed }));
      await reloadTypes();
      onSaved?.(data.type?.name || trimmed);
      return data.type?.name || trimmed;
    } catch (e2) {
      const detail = e2?.response?.data?.detail || t("search.type_add_failed");
      toast.error(detail);
      return null;
    } finally { setSavingType(false); }
  }, [reloadTypes, t]);

  const deleteCustomType = useCallback(async (name, onDeleted) => {
    if (!window.confirm(t("search.type_delete_confirm", { name }))) return;
    try {
      await axios.delete(`${API}/business-types/${encodeURIComponent(name)}`);
      toast.success(t("search.type_deleted", { name }));
      onDeleted?.(name);
      await reloadTypes();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || t("search.type_delete_failed");
      toast.error(detail);
    }
  }, [reloadTypes, t]);

  return { types, builtin, custom, savingType, reloadTypes, saveNewType, deleteCustomType };
}

/** Manages the 4 advanced filter knobs (rating, reviews, score, website). */
export function useAdvancedFilters() {
  const [ratingMin, setRatingMin] = useState(0);
  const [reviewsMin, setReviewsMin] = useState(0);
  const [scoreMax, setScoreMax] = useState(100);
  const [hasWebsite, setHasWebsite] = useState("");

  const reset = () => { setRatingMin(0); setReviewsMin(0); setScoreMax(100); setHasWebsite(""); };
  const advActive = (ratingMin > 0) || (reviewsMin > 0) || (scoreMax < 100) || !!hasWebsite;
  const advCount = [ratingMin > 0, reviewsMin > 0, scoreMax < 100, !!hasWebsite].filter(Boolean).length;

  return {
    ratingMin, setRatingMin, reviewsMin, setReviewsMin,
    scoreMax, setScoreMax, hasWebsite, setHasWebsite,
    reset, advActive, advCount,
  };
}

/** Loads /business-categories and exposes a category-id selector. */
export function useBusinessCategories() {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  useEffect(() => {
    axios.get(`${API}/business-categories`)
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, []);
  const matchesCategory = (typeName) => {
    if (!categoryId) return true;
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return true;
    const t = (typeName || "").toLowerCase();
    return cat.matches.some((m) => t.includes(m.toLowerCase()));
  };
  return { categories, categoryId, setCategoryId, matchesCategory };
}
